import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  Download,
  Globe,
  History,
  LayoutDashboard,
  LifeBuoy,
  Menu,
  Scan,
  Send,
  Shield,
  Wallet,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { CurrencySelector } from '../../components/payment/shared/CurrencySelector';
import { AccountMenu } from '../../components/auth/AccountMenu';
import { useCurrency } from '../../hooks/useCurrency';
import { useAuth } from '../../contexts/AuthContext';

const NAV = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/payment' },
  { icon: Send, label: 'Send', href: '/payment/send' },
  { icon: Download, label: 'Receive', href: '/payment/receive' },
  { icon: Scan, label: 'Scan', href: '/payment/scan' },
  { icon: Briefcase, label: 'Services', href: '/payment/services' },
  { icon: Globe, label: 'International', href: '/payment/international' },
  { icon: Wallet, label: 'Wallets', href: '/payment/wallets' },
  { icon: History, label: 'History', href: '/payment/history' },
  { icon: Shield, label: 'Security', href: '/payment/security' },
  { icon: LifeBuoy, label: 'Support', href: '/payment/support' },
];

function isActive(pathname: string, href: string) {
  return href === '/payment' ? pathname === '/payment' : pathname.startsWith(href);
}

export default function PaymentLayout() {
  const location = useLocation();
  const { base, setBase } = useCurrency();
  const { user, emailVerified } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const current = NAV.find((n) => isActive(location.pathname, n.href));

  const SidebarContent = () => (
    <>
      <div className="p-6 flex items-center justify-between">
        <Link to="/payment" className="text-2xl font-bold tracking-tighter">
          PESKEDO
        </Link>
        <button className="md:hidden" onClick={() => setMobileOpen(false)}>
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {NAV.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => setMobileOpen(false)}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all',
              isActive(location.pathname, item.href)
                ? 'bg-[#1A1A1A] text-white shadow-lg shadow-black/5'
                : 'text-[#666666] hover:bg-[#F0F0F0] hover:text-[#1A1A1A]',
            )}
          >
            <item.icon size={18} />
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t border-[#E5E5E5]">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#F9F9F9]">
          <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-white text-xs font-bold">
            {(user?.email ?? '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate">{user?.email ?? 'Account'}</p>
            <p className={cn('text-[10px] truncate', emailVerified ? 'text-green-600' : 'text-amber-600')}>
              {emailVerified ? 'Email verified' : 'Email not verified'}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F5F5F5] text-[#1A1A1A]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r border-[#E5E5E5] flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white flex flex-col animate-in">
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-[#E5E5E5] flex items-center justify-between px-4 md:px-8 shrink-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)}>
              <Menu size={20} />
            </button>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[#666666]">
              {current?.label ?? 'Payment'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySelector value={base} onChange={setBase} compact />
            <button className="relative p-2 text-[#666666] hover:text-[#1A1A1A] transition-colors">
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <AccountMenu />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
